// src/private/src/pages/app/clients/ClientSchedulePage.jsx
//
// Client-facing meeting booking page. Tier-driven quota, two-step AI
// deflection gate, calendar slot picker, and a request form for
// Starter / over-quota clients.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

import {
  approveMeetingRequest,
  cancelBooking,
  createBooking,
  createMeetingRequest,
  declineMeetingRequest,
  fetchAvailability,
  fetchBookings,
  fetchMeetingHistory,
  fetchMeetingPolicy,
  fetchMeetingStaffContext,
  issueMeetingGrant,
  rescheduleBooking,
  revokeMeetingGrant,
} from '@src/api/clientSchedule';
import { dayjs } from '@src/corelib';
import { useEffectiveUser } from '@src/components/layout/AppLayout/ClientViewSwitcher';
import { useMessage } from '@src/context/common';
import paths from '@src/paths';


// "Quick action" chips on the AI gate card. Each one deep-links into
// /agent-chat with a pre-filled prompt — these are intentionally the
// topics clients most often want a meeting for but that the agent can
// actually answer over chat. Tweak freely.
const AGENT_QUICK_ACTIONS = [
  {
    label: 'Update ZIP codes',
    prompt: 'I need to update my ZIP code coverage. Can you help me?',
  },
  {
    label: 'Update budget',
    prompt: 'I want to change my monthly budget. What are my options?',
  },
  {
    label: 'How are we pacing?',
    prompt: 'How are we pacing this month? Show me spend vs. plan.',
  },
  {
    label: 'Recent lead quality',
    prompt: 'Walk me through the quality of leads from the last 7 days.',
  },
];


// Browser-resolved IANA timezone (e.g. "America/New_York"). Falls back
// quietly if the browser doesn't expose Intl (very old environments).
function resolveBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (e) {
    return '';
  }
}


// Short display labels for the timezones office-hours rules realistically
// use. Anything else falls back to the raw IANA name.
const TZ_SHORT_LABELS = {
  'America/New_York': 'ET',
  'America/Chicago': 'CT',
  'America/Denver': 'MT',
  'America/Phoenix': 'MST',
  'America/Los_Angeles': 'PT',
};

// "Office hours: Tuesdays & Wednesdays, 1 – 4 PM ET" — built from the
// live AvailabilityRule rows in the policy payload so the header can't
// drift from what the slot engine actually offers.
function formatOfficeHours(rules) {
  if (!Array.isArray(rules) || rules.length === 0) return '';
  const groups = new Map();
  for (const rule of rules) {
    const key = `${rule.start_time}|${rule.end_time}|${rule.timezone}`;
    if (!groups.has(key)) groups.set(key, { ...rule, days: [] });
    const group = groups.get(key);
    if (!group.days.includes(rule.weekday_display)) {
      group.days.push(rule.weekday_display);
    }
  }
  const fmtTime = (t) =>
    dayjs(`2000-01-01T${t}`).format('h:mm A').replace(':00 ', ' ');
  const joinDays = (days) => {
    const plural = days.map((d) => `${d}s`);
    if (plural.length <= 1) return plural.join('');
    return `${plural.slice(0, -1).join(', ')} & ${plural[plural.length - 1]}`;
  };
  return Array.from(groups.values())
    .map(
      (g) =>
        `${joinDays(g.days)}, ${fmtTime(g.start_time)} – ${fmtTime(g.end_time)} ${TZ_SHORT_LABELS[g.timezone] || g.timezone}`,
    )
    .join(' · ');
}


function ClientSchedulePage() {
  const { clientSlug } = useParams();
  const message = useMessage();
  // Effective user: staff previewing as a client (or the demo client) see
  // the client's page, without the staff controls.
  const user = useEffectiveUser();
  const isStaff = Boolean(user?.is_staff || user?.is_superuser);

  // Data
  const [policy, setPolicy] = useState(null);
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [meetingHistory, setMeetingHistory] = useState([]);
  const [staffContext, setStaffContext] = useState(null); // staff-only

  // Async state
  const [loading, setLoading] = useState({
    policy: true,
    slots: false,
    bookings: true,
    history: true,
  });
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // UI state
  const [calendarRevealed, setCalendarRevealed] = useState(false);
  const [gateDismissed, setGateDismissed] = useState(false); // "Yes, that answered me"
  const [selectedDay, setSelectedDay] = useState(null); // YYYY-MM-DD
  const [pendingSlot, setPendingSlot] = useState(null); // ISO string
  const [bookingNotes, setBookingNotes] = useState('');
  const [guestEmails, setGuestEmails] = useState(''); // comma-separated
  // Booking being moved. The original stays booked until the new slot
  // is confirmed — abandoning the flow costs nothing.
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  // Post-booking confirmation: { booking, mode: 'booked'|'rescheduled',
  // oldStartsAt }. Rendered as a success card in place of the picker.
  const [justBooked, setJustBooked] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null); // booking pending confirm

  // Request-form state (Starter / over quota)
  const [requestTopic, setRequestTopic] = useState('');
  const [requestBody, setRequestBody] = useState('');
  const [requestWindow, setRequestWindow] = useState('');
  const [requestSubmitted, setRequestSubmitted] = useState(false);

  const pickerAnchorRef = useRef(null);

  // ────────────────────────────────────────────────────────────────────────
  // Loaders
  // ────────────────────────────────────────────────────────────────────────

  const loadPolicy = useCallback(async () => {
    setLoading((s) => ({ ...s, policy: true }));
    try {
      const res = await fetchMeetingPolicy(clientSlug);
      setPolicy(res.data);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not load meeting policy.');
    } finally {
      setLoading((s) => ({ ...s, policy: false }));
    }
  }, [clientSlug]);

  const loadBookings = useCallback(async () => {
    setLoading((s) => ({ ...s, bookings: true }));
    try {
      const res = await fetchBookings(clientSlug);
      setBookings(res.data || []);
    } catch (err) {
      // Soft-fail — page is still useful without history.
    } finally {
      setLoading((s) => ({ ...s, bookings: false }));
    }
  }, [clientSlug]);

  const loadMeetingHistory = useCallback(async () => {
    setLoading((s) => ({ ...s, history: true }));
    try {
      const res = await fetchMeetingHistory(clientSlug, { limit: 10 });
      setMeetingHistory(res.data?.meetings || []);
    } catch (err) {
      // Soft-fail — page is useful even if Zoom webhook isn't wired yet.
    } finally {
      setLoading((s) => ({ ...s, history: false }));
    }
  }, [clientSlug]);

  const loadSlots = useCallback(async () => {
    setLoading((s) => ({ ...s, slots: true }));
    try {
      // While rescheduling, unblock the booking's own window server-side so
      // times overlapping the current meeting can be offered as new targets.
      const res = await fetchAvailability(
        clientSlug,
        rescheduleTarget ? { exclude_booking: rescheduleTarget.id } : {},
      );
      const list = (res.data || []).map((s) => s.starts_at);
      setSlots(list);
      if (list.length > 0 && !selectedDay) {
        setSelectedDay(dayjs(list[0]).format('YYYY-MM-DD'));
      }
    } catch (err) {
      message.error(
        err?.response?.data?.detail || 'Could not load availability.',
      );
    } finally {
      setLoading((s) => ({ ...s, slots: false }));
    }
  }, [clientSlug, selectedDay, message, rescheduleTarget]);

  const loadStaffContext = useCallback(async () => {
    if (!isStaff) return;
    try {
      const res = await fetchMeetingStaffContext(clientSlug);
      setStaffContext(res.data);
    } catch (err) {
      // Soft-fail — the client-facing page still works without it.
    }
  }, [clientSlug, isStaff]);

  useEffect(() => {
    loadPolicy();
    loadBookings();
    loadMeetingHistory();
    loadStaffContext();
  }, [loadPolicy, loadBookings, loadMeetingHistory, loadStaffContext]);

  // Lazy-load availability: only when the client passes the AI gate, or
  // immediately if the calendar is the only thing on the page.
  useEffect(() => {
    if (calendarRevealed) loadSlots();
  }, [calendarRevealed, loadSlots]);

  // Refetch policy on tab focus so a client parked on this page picks up a
  // grant staff just issued (their request form flips to the calendar)
  // without a manual reload. Cheap — only fires when the tab regains focus.
  useEffect(() => {
    const onFocus = () => {
      loadPolicy();
      loadBookings();
      if (isStaff) loadStaffContext();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadPolicy, loadBookings, loadStaffContext, isStaff]);

  // ────────────────────────────────────────────────────────────────────────
  // Derived state
  // ────────────────────────────────────────────────────────────────────────

  const canSelfServe =
    policy?.allow_self_serve_booking && policy?.quota_remaining > 0;

  // Rescheduling is quota-neutral (the backend swaps the calendar event in
  // place), so the picker must open even at quota 0 — the NORMAL state for a
  // client whose active booking consumed their monthly quota. Without this,
  // the Reschedule button silently does nothing for exactly those clients.
  const canUseCalendar = canSelfServe || Boolean(rescheduleTarget);

  // Group slots by local YYYY-MM-DD so the day selector and time selector
  // can render quickly.
  const slotsByDay = useMemo(() => {
    const map = {};
    for (const iso of slots) {
      const key = dayjs(iso).format('YYYY-MM-DD');
      (map[key] = map[key] || []).push(iso);
    }
    return map;
  }, [slots]);

  const availableDays = useMemo(() => Object.keys(slotsByDay), [slotsByDay]);

  // A self-serve client staring at an empty calendar shouldn't dead-end:
  // offer them the request form too, not just non-self-serve tiers.
  const slotsExhausted =
    calendarRevealed && !loading.slots && availableDays.length === 0;
  const showRequestForm =
    policy &&
    policy.allow_request_form &&
    (!canSelfServe || slotsExhausted) &&
    !rescheduleTarget &&
    !justBooked;

  const officeHoursLabel = useMemo(
    () => formatOfficeHours(policy?.office_hours),
    [policy],
  );

  // ────────────────────────────────────────────────────────────────────────
  // Actions
  // ────────────────────────────────────────────────────────────────────────

  const handleBookingConfirm = async () => {
    if (!pendingSlot) return;
    // Split the comma-separated guest field into a clean email list.
    // Filter to anything that looks roughly email-shaped — final
    // validation happens server-side via DRF's EmailField.
    const parsedGuests = guestEmails
      .split(/[,;\s]+/)
      .map((e) => e.trim())
      .filter((e) => e && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));

    setSubmitting(true);
    try {
      if (rescheduleTarget) {
        // In-place move: quota-neutral, the backend replaces the
        // calendar event + Zoom meeting and carries guests over.
        const res = await rescheduleBooking(rescheduleTarget.id, {
          starts_at: pendingSlot,
        });
        setJustBooked({
          booking: res.data?.booking,
          mode: 'rescheduled',
          oldStartsAt: res.data?.old_starts_at,
        });
        setRescheduleTarget(null);
      } else {
        const res = await createBooking(clientSlug, {
          starts_at: pendingSlot,
          client_notes: bookingNotes,
          attendee_emails: parsedGuests,
        });
        setJustBooked({ booking: res.data?.booking, mode: 'booked' });
      }
      setPendingSlot(null);
      setBookingNotes('');
      setGuestEmails('');
      // The success card replaces the picker; refresh quota + history.
      await Promise.all([loadPolicy(), loadBookings()]);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || 'Could not book that slot.';
      message.error(detail);
      // A stale slot list is the usual cause — refresh it in place.
      if (err?.response?.data?.code === 'slot_unavailable') loadSlots();
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    setSubmitting(true);
    try {
      const res = await cancelBooking(cancelTarget.id, { reason: '' });
      message.success(
        res.data?.quota_was_restored
          ? 'Meeting cancelled — your quota was restored.'
          : 'Meeting cancelled.',
      );
      setCancelTarget(null);
      await Promise.all([loadPolicy(), loadBookings()]);
      if (calendarRevealed) loadSlots();
    } catch (err) {
      message.error(
        err?.response?.data?.detail || 'Could not cancel meeting.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Reschedule keeps the current booking in place and just opens the
  // picker; nothing changes until the client confirms the new slot.
  const handleReschedule = (booking) => {
    setJustBooked(null);
    setRescheduleTarget(booking);
    setCalendarRevealed(true);
    if (calendarRevealed) loadSlots();
    // Scroll to the picker so the next step is obvious.
    setTimeout(() => {
      pickerAnchorRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 100);
  };

  // Dismiss the success card back to the page's resting state.
  const handleSuccessDone = () => {
    setJustBooked(null);
    setCalendarRevealed(false);
    setGateDismissed(false);
  };

  const handleRequestSubmit = async () => {
    if (!requestTopic.trim()) {
      message.error('Please enter a topic.');
      return;
    }
    setSubmitting(true);
    try {
      await createMeetingRequest(clientSlug, {
        topic: requestTopic.trim(),
        body: requestBody.trim(),
        requested_window: requestWindow.trim(),
      });
      setRequestSubmitted(true);
      setRequestTopic('');
      setRequestBody('');
      setRequestWindow('');
      message.success('Request submitted — we\'ll follow up by email.');
    } catch (err) {
      message.error(
        err?.response?.data?.detail || 'Could not submit request.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Reload everything a staff grant/approval touched: the policy now
  // reports self-serve (so the client sees the calendar), the grant list
  // changed, and the slot list may need a refresh if it's already open.
  const handleStaffChanged = useCallback(async () => {
    await Promise.all([loadPolicy(), loadBookings(), loadStaffContext()]);
    if (calendarRevealed) loadSlots();
  }, [loadPolicy, loadBookings, loadStaffContext, calendarRevealed, loadSlots]);

  // ────────────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────────────

  if (loading.policy) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!policy) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          No meeting policy is configured for this client's tier.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 980, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Schedule a Meeting
      </Typography>
      {officeHoursLabel && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Office hours: {officeHoursLabel}
        </Typography>
      )}

      <QuotaStrip policy={policy} />

      {policy?.backend_data_connected === false && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 3 }}>
          <strong>Your sales data isn&apos;t connected yet.</strong> Until your CRM or backend data reaches the
          portal, we can only review leads and cost per lead on a call, not appointments, sales or revenue.
          Connecting it first makes your call far more useful.
        </Alert>
      )}

      {isStaff && (
        <StaffPanel
          clientSlug={clientSlug}
          context={staffContext}
          onChanged={handleStaffChanged}
        />
      )}

      {justBooked && (
        <BookingSuccessCard
          result={justBooked}
          canBookAnother={canSelfServe}
          onBookAnother={() => {
            setJustBooked(null);
            setCalendarRevealed(true);
            loadSlots();
          }}
          onDone={handleSuccessDone}
        />
      )}

      {canSelfServe && !justBooked && !calendarRevealed && !gateDismissed && (
        <AIGateCard
          clientSlug={clientSlug}
          onContinue={() => setCalendarRevealed(true)}
          onDismiss={() => setGateDismissed(true)}
        />
      )}

      {canSelfServe && !justBooked && !calendarRevealed && gateDismissed && (
        <GateThankYouCard onReopen={() => setGateDismissed(false)} />
      )}

      {canUseCalendar && !justBooked && calendarRevealed && (
        <Box ref={pickerAnchorRef}>
          {rescheduleTarget && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setRescheduleTarget(null)}
                >
                  Keep current time
                </Button>
              }
            >
              Moving your{' '}
              {dayjs(rescheduleTarget.starts_at).format('dddd, MMM D h:mm A')}{' '}
              meeting — your current time stays booked until you confirm a
              new one.
            </Alert>
          )}
          <BookingPicker
            loading={loading.slots}
            policy={policy}
            slotsByDay={slotsByDay}
            availableDays={availableDays}
            selectedDay={selectedDay}
            onSelectDay={setSelectedDay}
            onSelectSlot={(iso) => setPendingSlot(iso)}
          />
        </Box>
      )}

      {showRequestForm && (
        <RequestForm
          policy={policy}
          slotsExhausted={slotsExhausted && canSelfServe}
          topic={requestTopic}
          body={requestBody}
          window={requestWindow}
          submitted={requestSubmitted}
          submitting={submitting}
          onTopicChange={setRequestTopic}
          onBodyChange={setRequestBody}
          onWindowChange={setRequestWindow}
          onSubmit={handleRequestSubmit}
          onReset={() => setRequestSubmitted(false)}
        />
      )}

      <UpcomingBookings
        bookings={bookings}
        loading={loading.bookings}
        rescheduleTargetId={rescheduleTarget?.id}
        onCancel={(b) => setCancelTarget(b)}
        onReschedule={handleReschedule}
      />

      <PastMeetingsTimeline
        bookings={bookings}
        meetings={meetingHistory}
        loading={loading.bookings || loading.history}
      />

      <CancelledMeetingsAccordion
        bookings={bookings}
        loading={loading.bookings}
      />

      <ConfirmDialog
        open={Boolean(pendingSlot)}
        slot={pendingSlot}
        policy={policy}
        rescheduleTarget={rescheduleTarget}
        notes={bookingNotes}
        guests={guestEmails}
        submitting={submitting}
        onNotesChange={setBookingNotes}
        onGuestsChange={setGuestEmails}
        onCancel={() => {
          setPendingSlot(null);
          setBookingNotes('');
          setGuestEmails('');
        }}
        onConfirm={handleBookingConfirm}
      />

      <CancelDialog
        booking={cancelTarget}
        policy={policy}
        submitting={submitting}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancelConfirm}
      />
    </Box>
  );
}


// ────────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────────

// Three rendering modes:
//   1. Welcome-bonus  — active-new clients (≤30 days, derived from spend)
//      get one complimentary 30-min meeting regardless of tier. Header
//      reads "NEW CLIENT WELCOME" with the window expiration callout.
//   2. Soft request   — tiers with no self-serve allowance (Starter).
//      We drop the blunt "0 of 0 meetings" line in favor of a friendly
//      "submit a request" lead-in plus a tier-progression note showing
//      what spend unlocks the next tier's meetings.
//   3. Standard       — tiers with meetings/month > 0. Existing behavior:
//      "X of Y used this month" + progress bar + late-cancel callout.
function QuotaStrip({ policy }) {
  if (policy.is_access_grant) {
    return <AccessGrantStrip policy={policy} />;
  }
  if (policy.is_welcome_bonus) {
    return <WelcomeBonusStrip policy={policy} />;
  }
  if (!(policy.meetings_per_month > 0)) {
    return <SoftRequestStrip policy={policy} />;
  }
  return <StandardQuotaStrip policy={policy} />;
}


// Shown to a client whose account team opened a one-off meeting for them
// (a staff bypass or an approved request). Friendly, no tier framing.
function AccessGrantStrip({ policy }) {
  const remaining = policy.quota_remaining ?? 0;
  const expires = policy.access_grant_expires_at
    ? dayjs(policy.access_grant_expires_at).format('MMMM D')
    : null;
  const allBooked = remaining === 0;
  return (
    <Card sx={{ mb: 3, borderLeft: 4, borderColor: 'success.main' }}>
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
          <LockOpenIcon color="success" fontSize="small" />
          <Typography variant="overline" color="success.main">
            Meeting unlocked for you
          </Typography>
        </Stack>
        <Typography variant="h6">
          {allBooked
            ? 'Your meeting is booked'
            : 'Your account team opened up a meeting — pick a time below'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {policy.duration_minutes} min
          {expires ? ` · please book by ${expires}` : ''}
        </Typography>
      </CardContent>
    </Card>
  );
}


function WelcomeBonusStrip({ policy }) {
  const expires = policy.welcome_window_end
    ? dayjs(policy.welcome_window_end).format('MMMM D')
    : null;
  const used = policy.quota_used || 0;
  const allowed = policy.meetings_per_month || 1;
  const remaining = policy.quota_remaining ?? Math.max(0, allowed - used);
  const allBooked = remaining === 0;
  return (
    <Card sx={{ mb: 3, borderLeft: 4, borderColor: 'primary.main' }}>
      <CardContent>
        <Typography variant="overline" color="primary">
          New client welcome
        </Typography>
        <Typography variant="h6">
          {allBooked
            ? `${used} of ${allowed} meeting booked`
            : `${used} of ${allowed} meeting available — book your complimentary 30-min strategy call`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Welcome offer · {policy.duration_minutes || 30} min
          {expires ? ` · expires ${expires}` : ''}
        </Typography>
      </CardContent>
    </Card>
  );
}


function SoftRequestStrip({ policy }) {
  const basis = policy.basis_spend ? Number(policy.basis_spend) : null;
  const nextThreshold = policy.next_tier_threshold
    ? Number(policy.next_tier_threshold)
    : null;
  const nextMeetings = policy.next_tier_meetings_per_month;
  const nextName = policy.next_tier_name;
  const showProgression =
    basis != null && nextThreshold != null && nextName && nextMeetings > 0;
  const gap = showProgression ? Math.max(0, nextThreshold - basis) : 0;
  const fmt = (n) =>
    `$${Math.round(n).toLocaleString('en-US')}`;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
          <ChatBubbleOutlineIcon color="primary" fontSize="small" />
          <Typography variant="h6">Need to talk something through?</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Submit a request below and we'll follow up by email within 1 business
          day to find a time that works.
        </Typography>
        {showProgression && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1.5 }}
          >
            Scheduled office hours are part of the {nextName} tier and above.
            Your trailing-3-month avg is {fmt(basis)}/mo —
            {gap > 0
              ? ` ${fmt(gap)}/mo to unlock ${nextMeetings} meeting${nextMeetings === 1 ? '' : 's'}/mo.`
              : ` you're at the threshold — your tier will update on the next refresh.`}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}


function StandardQuotaStrip({ policy }) {
  const pct = policy.meetings_per_month
    ? Math.min(100, (policy.quota_used / policy.meetings_per_month) * 100)
    : 0;
  const resets = dayjs(policy.quota_resets_at).format('MMMM D');
  const lateCancelCount = policy.quota_late_cancellations || 0;
  const cancelWindowHours = policy.cancel_restores_quota_within_hours || 24;
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="overline" color="text.secondary">
              {policy.tier_name} tier
            </Typography>
            <Typography variant="h6">
              {policy.quota_used} of {policy.meetings_per_month} meetings used this month
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {`${policy.quota_remaining} remaining · ${policy.duration_minutes} min each · resets ${resets}`}
            </Typography>
            {lateCancelCount > 0 && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Includes {lateCancelCount} cancelled within {cancelWindowHours}h
                </Typography>
                <Tooltip
                  title={`Cancellations more than ${cancelWindowHours} hours before the meeting restore your quota. Cancellations inside that window still count toward your monthly total.`}
                  arrow
                >
                  <InfoOutlinedIcon
                    sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }}
                  />
                </Tooltip>
              </Stack>
            )}
          </Box>
          <Box sx={{ width: { xs: '100%', sm: 240 } }}>
            <LinearProgress
              variant="determinate"
              value={pct}
              color={policy.quota_remaining > 0 ? 'primary' : 'warning'}
            />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}


function AIGateCard({ clientSlug, onContinue, onDismiss }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [freeText, setFreeText] = useState('');

  // Build the deep-link URL to the agent chat page. `client=<slug>`
  // pre-selects the right client and `autosend=1` makes the agent fire
  // the prompt immediately instead of just pre-filling the input.
  const askAgent = (prompt) => {
    const trimmed = (prompt || '').trim();
    if (!trimmed) return;
    const params = new URLSearchParams({
      prompt: trimmed,
      autosend: '1',
    });
    if (clientSlug) params.set('client', clientSlug);
    navigate(`${paths.agentChat}?${params.toString()}`);
  };

  const handleFreeSubmit = (e) => {
    e?.preventDefault?.();
    askAgent(freeText);
  };

  return (
    <Card
      sx={{
        mb: 3,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        border: 1,
        borderColor: alpha(theme.palette.primary.main, 0.2),
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <SmartToyIcon color="primary" />
          <Typography variant="h6">Have a quick question?</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Ask anything below — Guru has live access to your account
          and answers in seconds.
        </Typography>

        <Box component="form" onSubmit={handleFreeSubmit} sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Ask a question…"
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            InputProps={{
              endAdornment: (
                <Button
                  type="submit"
                  variant="contained"
                  size="small"
                  disabled={!freeText.trim()}
                  endIcon={<SendIcon />}
                  sx={{ ml: 1, whiteSpace: 'nowrap' }}
                >
                  Ask
                </Button>
              ),
            }}
          />
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Or try one of these
        </Typography>
        <Stack spacing={1} sx={{ mb: 3 }}>
          {AGENT_QUICK_ACTIONS.map((action) => (
            <Button
              key={action.label}
              variant="outlined"
              color="primary"
              startIcon={<ChatBubbleOutlineIcon />}
              onClick={() => askAgent(action.prompt)}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                py: 1.25,
                px: 2,
                borderRadius: 999,
                fontWeight: 400,
                color: 'text.primary',
                borderColor: alpha(theme.palette.primary.main, 0.25),
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  borderColor: alpha(theme.palette.primary.main, 0.45),
                },
              }}
            >
              {action.label}
            </Button>
          ))}
        </Stack>

        <Divider sx={{ mb: 2 }} />
        <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
          Still need a 1:1?
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button
            variant="outlined"
            color="primary"
            onClick={onDismiss}
            sx={{ flex: { sm: 1 } }}
          >
            No — thanks, all set
          </Button>
          <Button
            variant="contained"
            color="primary"
            endIcon={<EventAvailableIcon />}
            onClick={onContinue}
            sx={{ flex: { sm: 1 } }}
          >
            Yes — show me available times
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}


function GateThankYouCard({ onReopen }) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Typography variant="body1">
            Glad that helped! You can come back any time.
          </Typography>
          <Button size="small" onClick={onReopen}>
            Actually, I do need a meeting
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}


// Buckets a day's slots into local time-of-day sections so a dense day
// scans in one glance instead of one long chip soup.
function groupSlotsByPeriod(isoList) {
  const sections = [
    { label: 'Morning', slots: [] },
    { label: 'Afternoon', slots: [] },
    { label: 'Evening', slots: [] },
  ];
  for (const iso of isoList) {
    const hour = dayjs(iso).hour();
    if (hour < 12) sections[0].slots.push(iso);
    else if (hour < 17) sections[1].slots.push(iso);
    else sections[2].slots.push(iso);
  }
  return sections.filter((s) => s.slots.length > 0);
}


function BookingPicker({
  loading,
  policy,
  slotsByDay,
  availableDays,
  selectedDay,
  onSelectDay,
  onSelectSlot,
}) {
  if (loading) {
    // Skeletons mirror the real layout so nothing jumps when slots land.
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Pick a time
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                width={92}
                height={64}
                sx={{ borderRadius: 2 }}
              />
            ))}
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                width={84}
                height={32}
                sx={{ borderRadius: 999 }}
              />
            ))}
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (availableDays.length === 0) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Alert severity="info">
            No open slots in the next {policy.max_horizon_days} days.
            {policy.allow_request_form
              ? ' Use the request form below and we\'ll find a time that works.'
              : ' Please check back soon — new times open up regularly.'}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const slotsForSelected = selectedDay ? slotsByDay[selectedDay] || [] : [];
  const periods = groupSlotsByPeriod(slotsForSelected);
  const browserTz = resolveBrowserTimezone();

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Pick a time
        </Typography>

        <Typography variant="overline" color="text.secondary">
          Day
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 3, mt: 0.5 }}
        >
          {availableDays.map((day) => {
            const d = dayjs(day);
            const count = (slotsByDay[day] || []).length;
            const selected = selectedDay === day;
            return (
              <Button
                key={day}
                onClick={() => onSelectDay(day)}
                variant={selected ? 'contained' : 'outlined'}
                color="primary"
                sx={{
                  flexDirection: 'column',
                  minWidth: 92,
                  py: 0.75,
                  px: 1.5,
                  lineHeight: 1.3,
                  textTransform: 'none',
                  borderRadius: 2,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {d.format('ddd')} · {d.format('MMM D')}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>
                  {count} {count === 1 ? 'time' : 'times'}
                </Typography>
              </Button>
            );
          })}
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          alignItems="baseline"
          justifyContent="space-between"
          sx={{ mb: 0.5 }}
        >
          <Typography variant="overline" color="text.secondary">
            Time
          </Typography>
          {browserTz && (
            <Typography variant="caption" color="text.secondary">
              Times shown in your timezone: {browserTz}
            </Typography>
          )}
        </Stack>
        <Stack spacing={1.5}>
          {periods.map((period) => (
            <Box key={period.label}>
              {periods.length > 1 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.5 }}
                >
                  {period.label}
                </Typography>
              )}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {period.slots.map((iso) => (
                  <Chip
                    key={iso}
                    label={dayjs(iso).format('h:mm A')}
                    onClick={() => onSelectSlot(iso)}
                    clickable
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}


// Per-tier "what to expect" copy in the confirm dialog. Falls back to
// the generic line if the tier name doesn't match the known set.
function tierExpectationCopy(tierName) {
  const name = (tierName || '').toLowerCase();
  if (name.includes('elite')) {
    return 'Your account strategist will join with the latest performance data. Add anyone else from your team who should be there.';
  }
  if (name.includes('growth')) {
    return 'Your account lead will walk through your results and answer your questions. Add anyone else from your team who should attend.';
  }
  if (name.includes('scaling')) {
    return 'A focused 15-minute working session — best for one specific question. Use the notes field below so we come in prepared.';
  }
  return 'You\'ll get an email invite with the Zoom join link. Add anyone else who should attend below — they\'ll be invited too.';
}


function ConfirmDialog({
  open,
  slot,
  policy,
  rescheduleTarget,
  notes,
  guests,
  submitting,
  onNotesChange,
  onGuestsChange,
  onCancel,
  onConfirm,
}) {
  if (!slot) return null;
  const isReschedule = Boolean(rescheduleTarget);
  const durationMinutes = isReschedule
    ? rescheduleTarget.duration_minutes
    : policy.duration_minutes;
  const start = dayjs(slot);
  const end = start.add(durationMinutes, 'minute');
  const expectationCopy = tierExpectationCopy(policy.tier_name);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isReschedule ? 'Confirm new time' : 'Confirm meeting'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {isReschedule && (
            <Box>
              <Typography variant="overline" color="text.secondary">
                Current time
              </Typography>
              <Typography
                variant="body1"
                sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
              >
                {dayjs(rescheduleTarget.starts_at).format(
                  'dddd, MMMM D · h:mm A',
                )}
              </Typography>
            </Box>
          )}
          <Box>
            <Typography variant="overline" color="text.secondary">
              {isReschedule ? 'New time' : 'When'}
            </Typography>
            <Typography variant="body1">
              {start.format('dddd, MMMM D')}
            </Typography>
            <Typography variant="body1">
              {start.format('h:mm A')} – {end.format('h:mm A')} ({durationMinutes} min)
            </Typography>
          </Box>
          {isReschedule ? (
            <Alert severity="info">
              Everyone on the invite gets the new time automatically, and
              this doesn't use any extra quota.
            </Alert>
          ) : (
            <>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  What you can expect
                </Typography>
                <Typography variant="body2">{expectationCopy}</Typography>
              </Box>
              <TextField
                label="Add guests (optional)"
                value={guests}
                onChange={(e) => onGuestsChange(e.target.value)}
                fullWidth
                placeholder="email1@company.com, email2@company.com"
                helperText="Comma-separated email addresses. They'll get the same invite."
              />
              <TextField
                label="What would you like to discuss? (optional)"
                multiline
                minRows={3}
                value={notes}
                onChange={(e) => onNotesChange(e.target.value)}
                fullWidth
                placeholder="A short note helps us prep — e.g. 'Wondering about creative refresh strategy.'"
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>
          {isReschedule ? 'Back' : 'Cancel'}
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : <EventAvailableIcon />}
        >
          {isReschedule ? 'Move meeting' : 'Book meeting'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// Full-width confirmation moment after a booking/reschedule lands — the
// peak of the flow deserves more than a toast. Links straight to the
// calendar event; the email invite is called out for everyone else.
function BookingSuccessCard({ result, canBookAnother, onBookAnother, onDone }) {
  const booking = result.booking;
  if (!booking) return null;
  const start = dayjs(booking.starts_at);
  const moved = result.mode === 'rescheduled';

  return (
    <Card
      sx={{
        mb: 3,
        borderLeft: 4,
        borderColor: 'success.main',
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <CheckCircleOutlineIcon color="success" fontSize="large" />
          <Box>
            <Typography variant="h6">
              {moved ? 'Meeting moved' : 'Meeting booked'}
            </Typography>
            <Typography variant="body1">
              {start.format('dddd, MMMM D')} · {start.format('h:mm A')} (
              {booking.duration_minutes} min)
            </Typography>
          </Box>
        </Stack>
        {moved && result.oldStartsAt && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Moved from {dayjs(result.oldStartsAt).format('dddd, MMMM D · h:mm A')}.
            Everyone on the invite was notified.
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A calendar invite with the join link is on its way to your email.
          We'll come prepared — see you there.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          {booking.gcal_html_link && (
            <Button
              variant="outlined"
              startIcon={<CalendarMonthIcon />}
              href={booking.gcal_html_link}
              target="_blank"
              rel="noreferrer"
            >
              View in calendar
            </Button>
          )}
          {(booking.zoom_join_url || booking.gcal_meet_link) && (
            <Button
              variant="outlined"
              startIcon={<VideoCallIcon />}
              href={booking.zoom_join_url || booking.gcal_meet_link}
              target="_blank"
              rel="noreferrer"
            >
              Join link
            </Button>
          )}
          {canBookAnother && (
            <Button onClick={onBookAnother}>Book another</Button>
          )}
          <Button variant="contained" color="success" onClick={onDone}>
            Done
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}


// Styled replacement for window.confirm: states the quota consequence
// up front (restore vs consume) so the client knows before committing.
function CancelDialog({ booking, policy, submitting, onClose, onConfirm }) {
  if (!booking) return null;
  const start = dayjs(booking.starts_at);
  const restoreHours = policy?.cancel_restores_quota_within_hours ?? 24;
  const willRestore = start.diff(dayjs(), 'hour', true) > restoreHours;

  return (
    <Dialog open onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Cancel this meeting?</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {start.format('dddd, MMMM D · h:mm A')} ({booking.duration_minutes} min)
        </Typography>
        <Alert severity={willRestore ? 'success' : 'warning'}>
          {willRestore
            ? 'Plenty of notice — this meeting will be returned to your monthly quota.'
            : `This meeting starts within ${restoreHours} hours, so it will still count toward your monthly quota.`}
        </Alert>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          Just need a different time? Use Reschedule instead — it keeps your
          quota and moves everyone to the new time.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Keep meeting
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={onConfirm}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          Cancel meeting
        </Button>
      </DialogActions>
    </Dialog>
  );
}


function RequestForm({
  policy,
  slotsExhausted,
  topic,
  body,
  window: windowText,
  submitted,
  submitting,
  onTopicChange,
  onBodyChange,
  onWindowChange,
  onSubmit,
  onReset,
}) {
  const isOverQuota =
    policy.allow_self_serve_booking && policy.quota_remaining === 0;
  const resets = dayjs(policy.quota_resets_at).format('MMMM D');

  let leadCopy;
  if (slotsExhausted) {
    // Self-serve client staring at an empty calendar — the form is
    // their path to a time outside the posted windows.
    leadCopy =
      'No open slots right now — tell us what you need and when works for you, and we\'ll set something up by email.';
  } else if (isOverQuota) {
    leadCopy = `You've used your quota for this month — it resets on ${resets}. In the meantime, tell us what you need and we'll follow up by email.`;
  } else {
    leadCopy =
      'Tell us what you\'d like to cover and we\'ll follow up by email within 1 business day.';
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Request a meeting
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {leadCopy}
        </Typography>

        {submitted ? (
          <Alert severity="success" action={
            <Button color="inherit" size="small" onClick={onReset}>
              Submit another
            </Button>
          }>
            Request submitted. We'll follow up by email shortly.
          </Alert>
        ) : (
          <Stack spacing={2}>
            <TextField
              label="What do you want to discuss?"
              value={topic}
              onChange={(e) => onTopicChange(e.target.value)}
              required
              fullWidth
              placeholder="e.g. Creative refresh strategy for Q3"
            />
            <TextField
              label="Details (optional)"
              value={body}
              onChange={(e) => onBodyChange(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              placeholder="Context that helps us prep ahead of time."
            />
            <TextField
              label="When would work for you? (optional)"
              value={windowText}
              onChange={(e) => onWindowChange(e.target.value)}
              fullWidth
              placeholder="e.g. Thursday afternoon, next week"
            />
            <Box>
              <Button
                variant="contained"
                onClick={onSubmit}
                disabled={submitting || !topic.trim()}
                startIcon={submitting ? <CircularProgress size={16} /> : null}
              >
                Submit request
              </Button>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}


function UpcomingBookings({
  bookings,
  loading,
  rescheduleTargetId,
  onCancel,
  onReschedule,
}) {
  if (loading) return null;
  const now = dayjs();
  const upcoming = (bookings || []).filter(
    (b) => b.status === 'active' && dayjs(b.starts_at).isAfter(now),
  );
  if (upcoming.length === 0) return null;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Upcoming meetings
        </Typography>
        <Stack divider={<Divider flexItem />} spacing={2}>
          {upcoming.map((b) => (
            <BookingRow
              key={b.id}
              booking={b}
              isRescheduling={b.id === rescheduleTargetId}
              onCancel={onCancel}
              onReschedule={onReschedule}
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}


// Joins completed bookings + Zoom meeting notes by booking_id into a
// single time-sorted timeline so the client sees "Tuesday call → here's
// what we discussed" as one row instead of two. Cancellations are
// shown separately in `CancelledMeetingsAccordion` so they don't muddy
// the record of meetings that actually happened.
function PastMeetingsTimeline({ bookings, meetings, loading }) {
  const rows = useMemo(() => {
    const now = dayjs();
    const notesByBookingId = new Map();
    const unmatchedNotes = [];
    for (const note of meetings || []) {
      if (note.booking_id) {
        notesByBookingId.set(note.booking_id, note);
      } else {
        unmatchedNotes.push(note);
      }
    }

    // Completed = explicitly flipped to COMPLETED by the Zoom webhook,
    // OR a still-ACTIVE booking whose start time has passed (webhook
    // hasn't fired yet but the meeting demonstrably happened).
    const pastBookings = (bookings || []).filter(
      (b) =>
        b.status === 'completed' ||
        (b.status === 'active' && dayjs(b.starts_at).isBefore(now)),
    );

    const merged = pastBookings.map((b) => ({
      kind: 'booking',
      key: `booking-${b.id}`,
      sortDate: b.starts_at,
      booking: b,
      note: notesByBookingId.get(b.id) || null,
    }));

    for (const note of unmatchedNotes) {
      merged.push({
        kind: 'note',
        key: `note-${note.id}`,
        sortDate: note.started_at,
        booking: null,
        note,
      });
    }

    merged.sort((a, b) => {
      const ad = a.sortDate ? new Date(a.sortDate).getTime() : 0;
      const bd = b.sortDate ? new Date(b.sortDate).getTime() : 0;
      return bd - ad;
    });
    return merged.slice(0, 10);
  }, [bookings, meetings]);

  if (loading) return null;
  if (rows.length === 0) return null;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Past meetings
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Summaries auto-generated by Zoom AI Companion. Guru uses them as context when answering your questions.
        </Typography>
        <Stack divider={<Divider flexItem />} spacing={2}>
          {rows.map((row) => (
            <PastMeetingRow key={row.key} row={row} />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}


function PastMeetingRow({ row }) {
  const { booking, note } = row;
  const when = row.sortDate ? dayjs(row.sortDate) : null;
  const title = note?.title || (booking ? `${booking.duration_minutes}-min meeting` : 'Meeting');
  const actionItems = Array.isArray(note?.action_items) ? note.action_items : [];

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        spacing={1}
      >
        <Typography variant="subtitle2">{title}</Typography>
        <Typography variant="caption" color="text.secondary">
          {when ? when.format('MMM D, h:mm A') : '—'}
          {note?.duration_minutes
            ? ` · ${note.duration_minutes} min`
            : booking?.duration_minutes
              ? ` · ${booking.duration_minutes} min`
              : ''}
        </Typography>
      </Stack>
      {booking && !note && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          No AI summary captured for this call.
        </Typography>
      )}
      {note?.summary && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
        >
          {note.summary}
        </Typography>
      )}
      {actionItems.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Action items:
          </Typography>
          <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
            {actionItems.slice(0, 5).map((item, idx) => {
              const text =
                typeof item === 'string' ? item : item?.text || JSON.stringify(item);
              return (
                <li key={idx}>
                  <Typography variant="body2" component="span">
                    {text}
                  </Typography>
                </li>
              );
            })}
          </ul>
        </Box>
      )}
      {note?.transcript_url && (
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            href={note.transcript_url}
            target="_blank"
            rel="noreferrer"
          >
            View transcript
          </Button>
        </Box>
      )}
    </Box>
  );
}


// Collapsible section listing cancelled bookings. Quota-consumed
// cancellations are flagged so the client can see exactly which
// late-cancel ate their quota when the quota strip says "Includes N
// cancelled within 24h".
function CancelledMeetingsAccordion({ bookings, loading }) {
  const cancelled = useMemo(() => {
    return (bookings || [])
      .filter(
        (b) =>
          b.status === 'cancelled_restored' || b.status === 'cancelled_consumed',
      )
      .sort((a, b) => {
        const ad = a.starts_at ? new Date(a.starts_at).getTime() : 0;
        const bd = b.starts_at ? new Date(b.starts_at).getTime() : 0;
        return bd - ad;
      })
      .slice(0, 15);
  }, [bookings]);

  if (loading) return null;
  if (cancelled.length === 0) return null;

  const consumedCount = cancelled.filter(
    (b) => b.status === 'cancelled_consumed',
  ).length;

  return (
    <Accordion sx={{ mb: 3 }} disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2" color="text.secondary">
          Cancelled meetings ({cancelled.length}
          {consumedCount > 0 ? ` · ${consumedCount} counted toward quota` : ''})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack divider={<Divider flexItem />} spacing={1.5}>
          {cancelled.map((b) => {
            const when = dayjs(b.starts_at);
            const consumed = b.status === 'cancelled_consumed';
            return (
              <Stack
                key={b.id}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'baseline' }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="body2">
                    {when.format('MMM D, h:mm A')} · {b.duration_minutes} min
                  </Typography>
                  {b.cancellation_reason && (
                    <Typography variant="caption" color="text.secondary">
                      {b.cancellation_reason}
                    </Typography>
                  )}
                </Box>
                <Chip
                  size="small"
                  label={consumed ? 'Counted toward quota' : 'Quota restored'}
                  color={consumed ? 'warning' : 'default'}
                  variant={consumed ? 'filled' : 'outlined'}
                />
              </Stack>
            );
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}


function BookingRow({ booking, isRescheduling, onCancel, onReschedule }) {
  const start = dayjs(booking.starts_at);
  const joinHref = booking.zoom_join_url || booking.gcal_meet_link;
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      alignItems={{ sm: 'center' }}
      justifyContent="space-between"
    >
      <Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body1">
            {start.format('dddd, MMM D · h:mm A')}
          </Typography>
          {isRescheduling && (
            <Chip size="small" color="info" label="Picking a new time…" />
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {booking.duration_minutes} min · {booking.status_display}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {joinHref && (
          <Button
            size="small"
            startIcon={<VideoCallIcon />}
            href={joinHref}
            target="_blank"
            rel="noreferrer"
          >
            Join
          </Button>
        )}
        {booking.gcal_html_link && (
          <Button
            size="small"
            startIcon={<CalendarMonthIcon />}
            href={booking.gcal_html_link}
            target="_blank"
            rel="noreferrer"
          >
            Add to calendar
          </Button>
        )}
        {onReschedule && (
          <Button
            size="small"
            startIcon={<EventRepeatIcon />}
            onClick={() => onReschedule(booking)}
          >
            Reschedule
          </Button>
        )}
        {onCancel && (
          <Button
            size="small"
            color="warning"
            onClick={() => onCancel(booking)}
          >
            Cancel
          </Button>
        )}
      </Stack>
    </Stack>
  );
}


// Staff-only control surface (rendered when the viewer is staff/superuser,
// including the "Viewing as" impersonation flow). Lets the operator approve
// a pending request — which emails the client a slot-picker link — or open
// a one-off booking outright (the tier/quota bypass). Both issue a
// MeetingAccessGrant; the client's calendar then unlocks with no tier change.
function StaffPanel({ clientSlug, context, onChanged }) {
  const theme = useTheme();
  const message = useMessage();
  const [meetings, setMeetings] = useState(1);
  const [days, setDays] = useState(7);
  const [notify, setNotify] = useState(false);
  const [busy, setBusy] = useState(false);

  const pending = context?.pending_requests || [];
  const activeGrant = context?.active_grant || null;
  const clampInt = (v, lo, hi, dflt) => {
    const n = parseInt(v, 10);
    if (Number.isNaN(n)) return dflt;
    return Math.max(lo, Math.min(hi, n));
  };
  const grantParams = () => ({
    meetings_allowed: clampInt(meetings, 1, 10, 1),
    expires_in_days: clampInt(days, 1, 90, 7),
  });

  const run = async (fn, successMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      message.success(typeof successMsg === 'function' ? successMsg(res) : successMsg);
      await onChanged?.();
    } catch (err) {
      message.error(err?.response?.data?.detail || 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  const doApprove = (requestId) =>
    run(
      () => approveMeetingRequest(requestId, grantParams()),
      'Approved — booking link emailed to the client.',
    );
  const doDecline = (requestId) =>
    run(() => declineMeetingRequest(requestId), 'Request declined.');
  const doBypass = () =>
    run(
      () => issueMeetingGrant(clientSlug, { ...grantParams(), notify }),
      (res) => {
        const emailed = res?.data?.notified_emails || [];
        return emailed.length
          ? `Booking opened — emailed ${emailed.join(', ')}.`
          : 'Booking opened — the client will see the calendar.';
      },
    );
  const doRevoke = (grantId) =>
    run(() => revokeMeetingGrant(grantId), 'Grant revoked.');

  return (
    <Card
      sx={{
        mb: 3,
        border: 1,
        borderColor: alpha(theme.palette.warning.main, 0.4),
        bgcolor: alpha(theme.palette.warning.main, 0.04),
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <AdminPanelSettingsIcon color="warning" />
          <Typography variant="h6">Staff controls</Typography>
          <Chip size="small" label="Only you see this" variant="outlined" />
        </Stack>

        {/* Shared grant parameters used by both approve + bypass. */}
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 2 }}
        >
          <TextField
            label="Meetings"
            type="number"
            size="small"
            value={meetings}
            onChange={(e) => setMeetings(e.target.value)}
            inputProps={{ min: 1, max: 10 }}
            sx={{ width: 110 }}
          />
          <TextField
            label="Expires in (days)"
            type="number"
            size="small"
            value={days}
            onChange={(e) => setDays(e.target.value)}
            inputProps={{ min: 1, max: 90 }}
            sx={{ width: 150 }}
          />
        </Stack>

        {/* Active grant */}
        {activeGrant && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                disabled={busy}
                onClick={() => doRevoke(activeGrant.id)}
              >
                Revoke
              </Button>
            }
          >
            Active grant: {activeGrant.meetings_allowed} meeting
            {activeGrant.meetings_allowed === 1 ? '' : 's'} ·{' '}
            {activeGrant.origin_display} · expires{' '}
            {dayjs(activeGrant.expires_at).format('MMM D')}
          </Alert>
        )}

        {/* Pending requests */}
        <Typography variant="overline" color="text.secondary">
          Pending requests
        </Typography>
        {pending.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No open requests from this client.
          </Typography>
        ) : (
          <Stack divider={<Divider flexItem />} spacing={1.5} sx={{ mb: 2, mt: 0.5 }}>
            {pending.map((req) => (
              <Stack
                key={req.id}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {req.topic}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {dayjs(req.created_at).format('MMM D')}
                    {req.requested_window ? ` · wants: ${req.requested_window}` : ''}
                    {req.status === 'email_sent' ? ' · link sent' : ''}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    disabled={busy}
                    startIcon={<EventAvailableIcon />}
                    onClick={() => doApprove(req.id)}
                  >
                    {req.status === 'email_sent' ? 'Re-send link' : 'Approve & send link'}
                  </Button>
                  <Button
                    size="small"
                    color="warning"
                    disabled={busy}
                    onClick={() => doDecline(req.id)}
                  >
                    Decline
                  </Button>
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* One-off bypass (no request needed) */}
        <Typography variant="overline" color="text.secondary">
          Open booking without a request
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Bypass tier/quota and let this client book now, even if they're
          Starter or out of quota.
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            color="warning"
            disabled={busy}
            startIcon={busy ? <CircularProgress size={16} /> : <LockOpenIcon />}
            onClick={doBypass}
          >
            Open a meeting
          </Button>
          <Tooltip
            title="Email the client's main contact(s) a slot-picker link. If off, they'll just see the calendar next time they open this page."
            arrow
          >
            <Button
              size="small"
              variant={notify ? 'contained' : 'text'}
              color="primary"
              onClick={() => setNotify((v) => !v)}
            >
              {notify ? 'Will email the client' : 'Email the client?'}
            </Button>
          </Tooltip>
        </Stack>
      </CardContent>
    </Card>
  );
}


export default ClientSchedulePage;
